/**
 * Atom: Pill
 *
 * Phase D · §07.2 — completes the 6-atom set
 * (Button → Pill → ListRow → KPI → TopBar → FocusCard).
 *
 * Spec-API status pill: `tone` (status token name) + optional `sub` label,
 * rendered with the locked geometry already used by `Badge` (15% bg tint,
 * 30% border tint, 5px dot, 11px / 500 Karla, full radius).
 *
 *   <Pill tone="active">Active</Pill>
 *   <Pill tone="onhold" sub="2d">On hold</Pill>
 *   <Pill tone="blocked" size="sm">Blocked</Pill>
 *
 * For semantic resolution by status string (e.g. value="in_progress"),
 * use `Badge` directly — Pill is the explicit-tone primitive new code
 * should reach for.
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

export type PillTone =
  | 'active'
  | 'onhold'
  | 'blocked'
  | 'done'
  | 'draft'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'

const TONE_COLOR: Record<PillTone, string> = {
  active:  'var(--st-active)',
  onhold:  'var(--st-onhold)',
  blocked: 'var(--st-blocked)',
  done:    'var(--st-done)',
  draft:   'var(--st-draft)',
  success: 'var(--st-success)',
  warning: 'var(--st-warning)',
  error:   'var(--st-error)',
  info:    'var(--st-info)',
  neutral: 'var(--text-muted)',
}

export interface PillProps {
  tone?: PillTone
  /** Optional secondary label (e.g. age, count) shown after a thin separator. */
  sub?: React.ReactNode
  size?: 'sm' | 'md'
  children?: React.ReactNode
  style?: React.CSSProperties
  className?: string
}

export const Pill: React.FC<PillProps> = ({
  tone = 'neutral',
  sub,
  size = 'md',
  children,
  style,
  className,
}) => {
  const c = TONE_COLOR[tone]
  // color-mix works with CSS variables — see Badge for the same locked recipe.
  const bgTint = `color-mix(in oklch, ${c} 15%, transparent)`
  const borderTint = `color-mix(in oklch, ${c} 30%, transparent)`
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: size === 'sm' ? '1px 6px' : '2px 8px',
        borderRadius: tokens.radius.full,
        fontSize: size === 'sm' ? 10 : 11,
        fontWeight: 500,
        fontFamily: tokens.typography.fontFamily.sans,
        backgroundColor: bgTint,
        color: c,
        border: `1px solid ${borderTint}`,
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        letterSpacing: 0.1,
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: c,
          flexShrink: 0,
        }}
      />
      {children}
      {sub != null && (
        <span style={{ opacity: 0.7, marginLeft: 4 }}>· {sub}</span>
      )}
    </span>
  )
}

export default Pill
